/**
 * <TitleProgress> — le titre de l'élève, et le chemin vers le suivant (molecule).
 *
 * L'anneau du livre (chap. 16, « Anneau de niveau ») avec ce que l'app a de vrai : pas de niveau,
 * mais un titre, gagné étape par étape dans une quête. L'arc dit où l'élève en est entre son titre
 * et le suivant ; au dernier titre d'une quête, l'anneau est plein et vert (« vert pour l'acquis »).
 *
 * `layout="ring"` ne garde que l'anneau, pour le coin d'un en-tête ; il se nomme alors pour un
 * lecteur d'écran. Elle ne presse pas : l'appelant l'entoure s'il ouvre le choix du titre.
 *
 * Au centre, la rosette pleine du jeu (`<GameGlyph>`, décision de Wajih, 15/10 : pas le glyphe fin
 * de l'interface), au ton de l'arc : corail en chemin, vert au dernier titre. C'est aussi la place
 * gardée du niveau : le jour où il revient, son nombre y prend la place de la rosette.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { CircularProgress } from "../../atoms/CircularProgress"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { GameGlyph } from "../GameGlyph"

/** Le diamètre de l'anneau : la taille de liste de l'avatar du livre. */
export const TITLE_RING_SIZE = 44
const RING_STROKE = 4
/** La rosette tient dans l'anneau avec un peu d'air : le trou de l'anneau moins un trait de chaque côté. */
const RING_GLYPH_SIZE = TITLE_RING_SIZE - 4 * RING_STROKE

export type TitleProgressProps = {
    /** Le titre de l'élève, par exemple « Ninja en herbe ». */
    title: string
    /** Le titre suivant de la même quête. Absent : l'élève a le dernier. */
    nextTitle?: string | null
    /** L'avancement vers le titre suivant, de 0 à 100. */
    progress: number
    /** `row` (défaut) : l'anneau puis le titre. `ring` : l'anneau seul. */
    layout?: "row" | "ring"
    testID?: string
}

export function TitleProgress(props: TitleProgressProps) {
    const { title, nextTitle, progress, layout = "row", testID } = props

    const hasNext = nextTitle != null && nextTitle !== ""
    const last = !hasNext
    const color = last ? "success" : "role"
    const value = hasNext ? progress : 100
    const next = hasNext ? `Prochain titre : ${nextTitle}` : null

    const ring = (
        <View style={styles.ring}>
            <CircularProgress
                value={value}
                size={TITLE_RING_SIZE}
                strokeWidth={RING_STROKE}
                color={color}
                trackColor="border"
            />
            <View style={styles.glyph} pointerEvents="none">
                <GameGlyph name="title" size={RING_GLYPH_SIZE} tone={last ? "acquired" : "gain"} plate={false} />
            </View>
        </View>
    )

    if (layout === "ring") {
        return (
            <View testID={testID} accessible accessibilityLabel={[`Titre : ${title}`, next].filter(Boolean).join(". ")}>
                {ring}
            </View>
        )
    }

    return (
        <Row testID={testID} gap="3" style={styles.root}>
            {ring}
            <Stack gap="0" style={styles.body}>
                <Text size="text-md" weight="bold" numberOfLines={1}>
                    {title}
                </Text>
                {next != null && (
                    <Text size="text-sm" color="text-muted" numberOfLines={1}>
                        {next}
                    </Text>
                )}
            </Stack>
        </Row>
    )
}

const styles = StyleSheet.create(() => ({
    root: { flexShrink: 1, minWidth: 0 },
    ring: { width: TITLE_RING_SIZE, height: TITLE_RING_SIZE, flexShrink: 0 },
    /** Le glyphe au centre de l'anneau. */
    glyph: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: "center",
        justifyContent: "center",
    },
    body: { flexShrink: 1, minWidth: 0 },
}))
